import { useEffect, useState, useCallback } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { LogOut, Phone, MapPin, CalendarCheck, CalendarDays, Loader2, Inbox, RefreshCw, MessageCircle } from "lucide-react";
import { Logo } from "@/components/Logo";
import { Calendar } from "@/components/ui/calendar";
import { toast } from "@/components/ui/sonner";
import PropertiesTab from "@/pages/admin/PropertiesTab";
import ProjectsTab from "@/pages/admin/ProjectsTab";
import ContentTab from "@/pages/admin/ContentTab";
import TeamTab from "@/pages/admin/TeamTab";
import "react-day-picker/dist/style.css";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STATUSES = ["New", "Contacted", "Closed"];

const statusCls = {
  New: "bg-gold/20 text-navy border-gold/50",
  Contacted: "bg-navy/5 text-navy border-navy/20",
  Closed: "bg-line/60 text-ink-soft border-line",
};

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [enquiries, setEnquiries] = useState(null);
  const [tab, setTab] = useState(["enquiries", "properties", "projects", "content", "team"].includes(searchParams.get("tab")) ? searchParams.get("tab") : "enquiries");

  const load = useCallback(async () => {
    const token = localStorage.getItem("rk_admin_token");
    if (!token) return navigate("/admin/login");
    try {
      const { data } = await axios.get(`${API}/enquiries`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setEnquiries(data);
    } catch (err) {
      if (err?.response?.status === 401) {
        localStorage.removeItem("rk_admin_token");
        navigate("/admin/login");
      } else {
        toast.error("Could not load enquiries. Please try again.");
        setEnquiries([]);
      }
    }
  }, [navigate]);

  useEffect(() => { load(); }, [load]);

  const setStatus = async (id, status) => {
    const token = localStorage.getItem("rk_admin_token");
    try {
      await axios.patch(`${API}/enquiries/${id}/status`, { status }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setEnquiries((list) => list.map((e) => (e.id === id ? { ...e, status } : e)));
      toast.success(`Marked as ${status}`);
    } catch {
      toast.error("Could not update status.");
    }
  };

  const logout = () => {
    localStorage.removeItem("rk_admin_token");
    localStorage.removeItem("rk_admin_user");
    navigate("/admin/login");
  };

  const stats = enquiries
    ? {
        total: enquiries.length,
        fresh: enquiries.filter((e) => e.status === "New").length,
        visits: enquiries.filter((e) => e.visit_date).length,
      }
    : null;

  const visits = enquiries
    ? [...enquiries]
        .filter((e) => e.visit_date)
        .sort((a, b) => `${a.visit_date} ${a.visit_time}`.localeCompare(`${b.visit_date} ${b.visit_time}`))
    : [];
  const bookedDates = visits.map((v) => new Date(`${v.visit_date}T00:00:00`));

  const waReply = (e) =>
    `https://wa.me/${e.phone.replace(/\D/g, "")}?text=${encodeURIComponent(
      `Hi ${e.name}, this is RK Real Estate following up on your ${e.interest} enquiry${
        e.location ? ` for ${e.location}` : ""
      }. When would be a good time to talk?`
    )}`;

  return (
    <div className="min-h-screen bg-warm" data-testid="admin-dashboard">
      <header className="bg-navy sticky top-0 z-40">
        <div className="max-w-[1280px] mx-auto px-4 sm:px-6 lg:px-8 h-[68px] flex items-center justify-between">
          <Link to="/" data-testid="admin-header-logo"><Logo dark /></Link>
          <div className="flex items-center gap-3">
            <Link to="/" className="text-[13px] text-white/60 hover:text-gold transition-colors hidden sm:block" data-testid="admin-view-site">
              View Website
            </Link>
            <button onClick={logout} data-testid="admin-logout-button"
              className="flex items-center gap-2 text-[13px] font-semibold text-white/85 border border-white/20 hover:border-gold hover:text-gold transition-colors rounded-lg px-4 py-2">
              <LogOut size={14} /> Logout
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-[1280px] mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-14">
        <div className="flex gap-2 mb-9 border-b border-line" data-testid="admin-tabs">
          {[["enquiries", "Enquiries"], ["properties", "Properties"], ["projects", "Projects"], ["content", "Website Content"], ["team", "Team"]].map(([key, label]) => (
            <button
              key={key}
              onClick={() => setTab(key)}
              data-testid={`admin-tab-${key}`}
              className={`px-5 py-3 text-sm font-semibold border-b-2 -mb-px transition-colors ${
                tab === key ? "border-gold text-navy" : "border-transparent text-ink-soft hover:text-navy"
              }`}
            >
              {label}
            </button>
          ))}
        </div>

        {tab === "properties" && <PropertiesTab />}

        {tab === "projects" && <ProjectsTab />}

        {tab === "content" && <ContentTab />}

        {tab === "team" && <TeamTab />}

        {tab === "enquiries" && (
        <>
        <div className="flex items-end justify-between gap-4 flex-wrap">
          <div>
            <span className="text-xs font-semibold uppercase tracking-[0.24em] text-gold">Enquiries Inbox</span>
            <h1 className="mt-2 font-display text-3xl sm:text-4xl font-semibold text-navy">Property Enquiries</h1>
          </div>
          <button onClick={load} data-testid="admin-refresh-button"
            className="flex items-center gap-2 text-[13px] font-semibold text-navy border border-line bg-white hover:border-gold transition-colors rounded-lg px-4 py-2.5">
            <RefreshCw size={14} /> Refresh
          </button>
        </div>

        {stats && (
          <div className="mt-8 grid grid-cols-3 gap-3 sm:gap-5 max-w-xl" data-testid="admin-stats">
            {[["Total", stats.total], ["New", stats.fresh], ["Site Visits", stats.visits]].map(([label, val]) => (
              <div key={label} className="bg-white rounded-xl border border-line p-4 sm:p-6">
                <p className="font-display text-2xl sm:text-3xl font-semibold text-navy">{val}</p>
                <p className="mt-1 text-[11px] sm:text-xs uppercase tracking-[0.14em] text-ink-soft">{label}</p>
              </div>
            ))}
          </div>
        )}

        {enquiries && enquiries.length > 0 && (
          <div className="mt-8 bg-white rounded-xl border border-line p-6 sm:p-8" data-testid="admin-visit-calendar">
            <div className="flex items-center gap-3">
              <span className="h-10 w-10 rounded-lg bg-navy flex items-center justify-center shrink-0">
                <CalendarDays size={17} className="text-gold" />
              </span>
              <div>
                <h2 className="font-display text-xl sm:text-2xl font-semibold text-navy">Site Visit Calendar</h2>
                <p className="text-[13px] text-ink-soft">Gold dates have requested site visits.</p>
              </div>
            </div>
            <div className="mt-6 grid grid-cols-1 lg:grid-cols-[auto_1fr] gap-8 items-start">
              <Calendar
                mode="single"
                modifiers={{ booked: bookedDates }}
                modifiersStyles={{ booked: { backgroundColor: "#C9A45C", color: "#0B1F33", fontWeight: 700, borderRadius: 8 } }}
                className="rounded-lg border border-line w-fit"
              />
              <div>
                <p className="text-[11px] font-bold uppercase tracking-[0.16em] text-ink-soft mb-3">Upcoming Visits</p>
                {visits.length === 0 ? (
                  <p className="text-sm text-ink-soft">No site visits requested yet.</p>
                ) : (
                  <div className="space-y-3">
                    {visits.map((v) => (
                      <div key={v.id} className="flex flex-wrap items-center justify-between gap-3 border border-line rounded-lg px-4 py-3" data-testid={`admin-visit-item-${v.id}`}>
                        <div>
                          <p className="text-sm font-semibold text-navy">
                            {v.name} <span className="font-normal text-ink-soft">· {v.interest}</span>
                          </p>
                          <p className="text-[12px] text-ink-soft mt-0.5">{v.phone}</p>
                        </div>
                        <span className="inline-flex items-center gap-2 text-[12px] font-semibold text-navy bg-gold/15 border border-gold/40 rounded-md px-3 py-1.5">
                          <CalendarCheck size={13} className="text-gold-dark" /> {v.visit_date} · {v.visit_time}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {enquiries === null ? (
          <div className="mt-16 flex justify-center text-ink-soft"><Loader2 className="animate-spin" size={26} /></div>
        ) : enquiries.length === 0 ? (
          <div className="mt-12 bg-white rounded-xl border border-line p-14 text-center" data-testid="admin-empty-state">
            <Inbox size={30} className="mx-auto text-gold" />
            <p className="mt-4 font-display text-xl text-navy">No enquiries yet</p>
            <p className="mt-1.5 text-sm text-ink-soft">New website enquiries will appear here automatically.</p>
          </div>
        ) : (
          <div className="mt-8 space-y-4" data-testid="admin-enquiries-list">
            {enquiries.map((e) => (
              <article key={e.id} className="bg-white rounded-xl border border-line p-6 sm:p-7 hover:border-gold-soft transition-colors" data-testid={`admin-enquiry-${e.id}`}>
                <div className="flex flex-wrap items-start justify-between gap-4">
                  <div>
                    <h3 className="font-display text-lg sm:text-xl font-semibold text-navy">{e.name}</h3>
                    <a href={`tel:${e.phone}`} className="mt-1 inline-flex items-center gap-1.5 text-sm text-ink-soft hover:text-gold transition-colors">
                      <Phone size={13} className="text-gold" /> {e.phone}
                    </a>
                  </div>
                  <div className="flex items-center gap-2.5 flex-wrap">
                    <a
                      href={waReply(e)}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={`admin-enquiry-whatsapp-${e.id}`}
                      className="inline-flex items-center gap-2 text-[12px] font-bold uppercase tracking-[0.08em] bg-[#25D366]/10 text-[#128C4A] border border-[#25D366]/40 rounded-md px-3 py-2 hover:bg-[#25D366]/20 transition-colors"
                    >
                      <MessageCircle size={13} /> Reply on WhatsApp
                    </a>
                    <select
                      value={e.status}
                      onChange={(ev) => setStatus(e.id, ev.target.value)}
                      data-testid={`admin-enquiry-status-${e.id}`}
                      className={`text-[12px] font-bold uppercase tracking-[0.1em] border rounded-md px-3 py-2 cursor-pointer focus:outline-none ${statusCls[e.status] || statusCls.New}`}
                    >
                      {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </div>
                </div>

                <div className="mt-4 flex flex-wrap gap-2 text-[12px] font-semibold">
                  <span className="bg-navy text-white rounded-md px-3 py-1.5">{e.interest}</span>
                  {e.propertyType && <span className="bg-warm border border-line rounded-md px-3 py-1.5">{e.propertyType}</span>}
                  {e.budget && <span className="bg-warm border border-line rounded-md px-3 py-1.5">Budget: {e.budget}</span>}
                  {e.location && (
                    <span className="bg-warm border border-line rounded-md px-3 py-1.5 inline-flex items-center gap-1">
                      <MapPin size={11} className="text-gold" /> {e.location}
                    </span>
                  )}
                </div>

                {e.message && <p className="mt-4 text-sm text-ink-soft leading-relaxed border-l-2 border-gold/60 pl-4">“{e.message}”</p>}

                {e.visit_date && (
                  <p className="mt-4 inline-flex items-center gap-2 text-[13px] font-semibold text-navy bg-gold/15 border border-gold/40 rounded-lg px-4 py-2" data-testid={`admin-enquiry-visit-${e.id}`}>
                    <CalendarCheck size={14} className="text-gold-dark" />
                    Site visit: {e.visit_date} · {e.visit_time}
                  </p>
                )}

                <p className="mt-4 text-[11px] text-ink-soft/70">
                  {new Date(e.created_at).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" })}
                </p>
              </article>
            ))}
          </div>
        )}
        </>
        )}
      </main>
    </div>
  );
}
